import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import { motion } from 'framer-motion';
import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiMapPin, FiUsers, FiTrash2 } from 'react-icons/fi';

type DragState = {
  id: string;
  offsetX: number;
  startX: number;
  dragging: boolean;
} | null;

interface EventItem {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  attendees?: string[];
  location?: string;
}

const Calendar: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState<string>('');
  const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [reminderMinutes, setReminderMinutes] = useState<number>(30);
  const [createInGoogle, setCreateInGoogle] = useState(false);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [icsText, setIcsText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const dragRef = useRef<DragState>(null);
  const socketRef = useRef<Socket | null>(null);
  // Edit fields
  const [editTitle, setEditTitle] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editAttendees, setEditAttendees] = useState('');

  // UI view state (does not affect backend logic)
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const load = async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (fromDate) qs.set('from', new Date(fromDate).toISOString());
      if (toDate) qs.set('to', new Date(toDate).toISOString());
      const resp = await api.get(`/calendar/events${qs.toString() ? `?${qs.toString()}` : ''}`);
      const list: EventItem[] = resp.data?.events || [];
      setEvents(list.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
    } catch (e) {
      console.error('Failed to load events', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    load(); 
    // Live updates via socket
    const base = (import.meta as any).env?.VITE_WS_URL || window.location.origin;
    const s = io(base, { transports: ['websocket'], path: '/socket.io/' });
    socketRef.current = s;
    const onCreated = (p: any) => setEvents(prev => {
      // Prevent duplicates if this client created it
      if (prev.some(ev => ev.id === p?.event?.id)) return prev;
      return [...prev, p.event].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    });
    const onUpdated = (p: any) => setEvents(prev => prev.map(ev => ev.id === p?.event?.id ? p.event : ev).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
    const onDeleted = (p: any) => setEvents(prev => prev.filter(ev => ev.id !== p?.eventId));
    s.on('calendar:event_created', onCreated);
    s.on('calendar:event_updated', onUpdated);
    s.on('calendar:event_deleted', onDeleted);
    return () => {
      s.off('calendar:event_created', onCreated);
      s.off('calendar:event_updated', onUpdated);
      s.off('calendar:event_deleted', onDeleted);
      s.disconnect();
    };
  }, []);

  const connectGoogle = async () => {
    try {
      const resp = await api.get('/calendar/google/auth');
      const url = resp.data?.url;
      if (url) window.open(url, '_blank');
    } catch (e) {
      toast.error('Failed to start Google auth');
    }
  };

  const syncGoogle = async () => {
    try {
      await api.post('/calendar/google/sync', {});
      toast.success('Google calendar synced');
      load();
    } catch (e) {
      toast.error('Sync failed');
    }
  };

  const handleCreate = async () => {
    if (!title || !startTime || !endTime) return;
    const payload = {
      title,
      startTime,
      endTime,
      description: description || undefined,
      attendees: attendees ? attendees.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      location: location || undefined,
      timezone,
      reminderMinutes,
    };
    try {
      const resp = createInGoogle ? await api.post('/calendar/google/events', payload) : await api.post('/calendar/events', payload);
      const ev: EventItem = resp.data?.event || payload as any;
      setEvents(prev => [...prev, ev].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      setTitle(''); setStartTime(''); setEndTime(''); setDescription(''); setLocation(''); setAttendees('');
      toast.success('Event created');
    } catch (e: any) {
      console.error('Create failed', e);
      toast.error('Failed to create event');
    }
  };

  const handleDelete = async (id: string) => {
    const prev = events;
    setEvents(prev => prev.filter(e => e.id !== id));
    try {
      await api.delete(`/calendar/events/${id}`);
      toast.success('Event deleted');
    } catch (e) {
      toast.error('Delete failed');
      setEvents(prev);
    }
  };

  const onMouseDown = (e: React.MouseEvent, id: string) => {
    dragRef.current = { id, offsetX: 0, startX: e.clientX, dragging: true };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current?.dragging) return;
    dragRef.current.offsetX = e.clientX - dragRef.current.startX;
  };
  const onMouseUp = async () => {
    if (!dragRef.current) return;
    // Simple horizontal drag adjusts start/end by 15 min increments
    const pixels = dragRef.current.offsetX;
    const steps = Math.round(pixels / 20); // 20px per 15 minutes
    if (steps !== 0) {
      let updatedEvent: EventItem | null = null;
      setEvents((prev: EventItem[]): EventItem[] => prev.map((ev: EventItem): EventItem => {
        const current = dragRef.current as DragState;
        if (!current || ev.id !== current.id) return ev;
        const start = new Date(ev.startTime);
        const end = new Date(ev.endTime);
        const deltaMs = steps * 15 * 60 * 1000;
        const newStart = new Date(start.getTime() + deltaMs).toISOString();
        const newEnd = new Date(end.getTime() + deltaMs).toISOString();
        updatedEvent = { ...ev, startTime: newStart, endTime: newEnd } as EventItem;
        return updatedEvent!;
      }).sort((a: EventItem, b: EventItem) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      try {
        if (updatedEvent) {
          const evp: any = updatedEvent as any;
          await api.put(`/calendar/events/${evp.id}`, {
            startTime: evp.startTime,
            endTime: evp.endTime,
          });
        }
      } catch (e) {
        toast.error('Failed to move event');
        // Reload to correct
        load();
      }
    }
    dragRef.current = null;
  };

  // Populate edit form when selecting an event
  useEffect(() => {
    if (!selectedId) {
      setEditTitle(''); setEditStartTime(''); setEditEndTime(''); setEditDescription(''); setEditLocation(''); setEditAttendees('');
      return;
    }
    const ev = events.find(e => e.id === selectedId);
    if (!ev) return;
    setEditTitle(ev.title || '');
    // datetime-local expects yyyy-MM-ddTHH:mm (no seconds, no Z)
    const fmt = (iso: string) => {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditStartTime(fmt(ev.startTime));
    setEditEndTime(fmt(ev.endTime));
    setEditDescription(ev.description || '');
    setEditLocation(ev.location || '');
    setEditAttendees((ev.attendees || []).join(', '));
  }, [selectedId, events]);

  const handleSaveEdit = async () => {
    if (!selectedId) return;
    const payload: any = {
      title: editTitle,
      startTime: editStartTime ? new Date(editStartTime).toISOString() : undefined,
      endTime: editEndTime ? new Date(editEndTime).toISOString() : undefined,
      description: editDescription,
      location: editLocation,
      attendees: editAttendees ? editAttendees.split(',').map(s => s.trim()).filter(Boolean) : [],
    };
    try {
      const resp = await api.put(`/calendar/events/${selectedId}`, payload);
      const updated: EventItem = resp.data?.event || { id: selectedId, ...payload } as any;
      setEvents(prev => prev.map(e => e.id === selectedId ? { ...e, ...updated } : e).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()));
      toast.success('Event updated');
    } catch (e) {
      toast.error('Failed to update event');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6" onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">Calendar</h1>
            <p className="text-gray-400">Create, edit, and delete events</p>
          </div>
          <div className="flex items-center gap-2">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700">
              <FiChevronLeft />
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setCurrentDate(new Date())} className="px-3 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700">
              Today
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} className="p-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700">
              <FiChevronRight />
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={connectGoogle} className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500">
              Connect Google
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={syncGoogle} className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500">
              Sync Google
            </motion.button>
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => window.open(`/api/calendar/events/export${fromDate||toDate?`?${new URLSearchParams({ ...(fromDate?{from:new Date(fromDate).toISOString()}:{}), ...(toDate?{to:new Date(toDate).toISOString()}:{}) }).toString()}`:''}`, '_blank')} className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">
              Export ICS
            </motion.button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-gray-300 flex items-center gap-2">
            <FiCalendar className="text-purple-400" />
            <span className="font-medium">
              {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
          </div>
          <div className="flex gap-2">
            {(['month','week'] as const).map(mode => (
              <motion.button
                key={mode}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setViewMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  viewMode === mode
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)} View
              </motion.button>
            ))}
            <input type="date" value={fromDate} onChange={e=>setFromDate(e.target.value)} className="px-2 py-1 rounded bg-gray-800 text-gray-300 border border-gray-700" />
            <input type="date" value={toDate} onChange={e=>setToDate(e.target.value)} className="px-2 py-1 rounded bg-gray-800 text-gray-300 border border-gray-700" />
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={load} className="px-3 py-1 rounded bg-gray-800 text-gray-300 hover:bg-gray-700">Apply</motion.button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {/* New Event Card */}
        <div
          className="md:col-span-1 rounded-2xl p-6 border"
          style={{
            background: 'rgba(30, 41, 59, 0.8)',
            backdropFilter: 'blur(20px)',
            borderColor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <h3 className="text-lg font-semibold mb-4 text-white">New Event</h3>
          <div className="space-y-3">
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Title" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
            <input type="datetime-local" value={startTime} onChange={e=>setStartTime(e.target.value)} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
            <input type="datetime-local" value={endTime} onChange={e=>setEndTime(e.target.value)} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
            <input value={location} onChange={e=>setLocation(e.target.value)} placeholder="Location" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
            <input value={attendees} onChange={e=>setAttendees(e.target.value)} placeholder="Attendees (comma-separated emails)" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
            <div className="grid grid-cols-2 gap-2">
              <select value={timezone} onChange={e=>setTimezone(e.target.value)} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white">
                {(Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone').map((tz: string) => (<option key={tz} value={tz}>{tz}</option>)) : <option value={timezone}>{timezone}</option>}
              </select>
              <input type="number" min={0} max={10080} value={reminderMinutes} onChange={e=>setReminderMinutes(Number(e.target.value))} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white" placeholder="Reminder (min)" />
            </div>
            <label className="flex items-center gap-2 text-gray-300 text-sm">
              <input type="checkbox" checked={createInGoogle} onChange={e=>setCreateInGoogle(e.target.checked)} className="rounded" />
              Create in Google Calendar
            </label>
            <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg px-3 py-2" onClick={handleCreate}>Create</motion.button>
            <div className="pt-3 border-t border-gray-700">
              <textarea value={icsText} onChange={e=>setIcsText(e.target.value)} placeholder="Paste ICS content to import" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 h-24" />
              <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} className="mt-2 w-full bg-gray-700 hover:bg-gray-600 text-white rounded-lg px-3 py-2" onClick={async()=>{ try { const { data } = await api.post('/calendar/events/import', { ics: icsText }); toast.success(`Imported ${data?.created||0} events`); setIcsText(''); load(); } catch { toast.error('ICS import failed'); } }}>Import ICS</motion.button>
            </div>
          </div>
        </div>

        {/* Events Card */}
        <div
          className="md:col-span-2 rounded-2xl p-6 border"
          style={{
            background: 'rgba(30, 41, 59, 0.8)',
            backdropFilter: 'blur(20px)',
            borderColor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <FiCalendar className="text-purple-400" /> Events
          </h3>
          {events.length === 0 && <p className="text-gray-400">No events yet</p>}
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {events.map(ev => (
              <motion.div
                key={ev.id}
                whileHover={{ y: -2, scale: 1.01 }}
                className={`group select-none p-4 border rounded-xl flex items-center justify-between cursor-grab active:cursor-grabbing transition-colors ${selectedId===ev.id?'bg-purple-500/10 border-purple-500/50':'bg-gray-800/60 border-gray-700 hover:border-gray-600'}`}
                onClick={()=>setSelectedId(ev.id)}
                onMouseDown={(e)=>onMouseDown(e, ev.id)}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white truncate">{ev.title}</div>
                  <div className="mt-0.5 text-sm text-gray-400 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><FiClock className="text-gray-500" /> {new Date(ev.startTime).toLocaleString()} - {new Date(ev.endTime).toLocaleString()}</span>
                  </div>
                  {(ev.location || ev.attendees?.length || ev.description) && (
                    <div className="text-xs text-gray-400 mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
                      {ev.location && <span className="inline-flex items-center gap-1"><FiMapPin className="text-gray-500" /> {ev.location}</span>}
                      {ev.attendees?.length ? <span className="inline-flex items-center gap-1"><FiUsers className="text-gray-500" /> {ev.attendees.length}</span> : null}
                      {ev.description && <span className="truncate inline-block max-w-[260px] align-bottom">{ev.description}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 pl-3">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 bg-gray-800/80 border border-gray-700 px-2 py-1 rounded hidden md:inline">Drag to move</span>
                  <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="p-2 rounded-lg bg-gray-800 text-red-400 hover:text-white hover:bg-red-600/80" onClick={()=>handleDelete(ev.id)}>
                    <FiTrash2 />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
          {selectedId && (
            <div className="mt-6 rounded-xl p-4 border border-gray-700 bg-gray-800/60">
              <h4 className="text-md font-semibold mb-3 text-white">Edit Selected Event</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={editTitle} onChange={e=>setEditTitle(e.target.value)} placeholder="Title" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
                <input type="datetime-local" value={editStartTime} onChange={e=>setEditStartTime(e.target.value)} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
                <input type="datetime-local" value={editEndTime} onChange={e=>setEditEndTime(e.target.value)} className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
                <input value={editLocation} onChange={e=>setEditLocation(e.target.value)} placeholder="Location" className="w-full rounded-lg px-3 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
                <input value={editAttendees} onChange={e=>setEditAttendees(e.target.value)} placeholder="Attendees (comma-separated emails)" className="w-full rounded-lg px-3 py-2 md:col-span-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
                <textarea value={editDescription} onChange={e=>setEditDescription(e.target.value)} placeholder="Description" className="w-full rounded-lg px-3 py-2 md:col-span-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-purple-500" />
              </div>
              <div className="mt-3 flex gap-3">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white" onClick={handleSaveEdit}>Save Changes</motion.button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="px-4 py-2 rounded-lg bg-gray-700 text-gray-200 hover:bg-gray-600" onClick={()=>setSelectedId(null)}>Cancel</motion.button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
